/**
 * What a social sign-in says when it doesn't sign in, on the website and in
 * the apps.
 *
 * The website hears why from `/login?sso_error=<code>`. An app hears it from
 * the API's answer to its sign-in, and both hear it from the answer to a
 * pending sign-up. The reasons are the same everywhere, so the words are one
 * definition here. Each platform renders a notice with its own `Alert`, whose
 * variants these tones match, and adds only what is its own, such as the
 * website's waitlist link.
 *
 * The tone follows the cause: neutral for a choice, warning for a retry, info
 * for a pointer, error for a sign-in that can't work.
 *
 * Data-only, so React Native can read it. Import it by subpath
 * (`shared/src/config/social-sign-in.config`), never through the shared root
 * barrel, which is not React Native-safe.
 */

import { API_ERROR_CODES } from '../types/api/errors';

import type {
  AccountFlowResult,
  SocialProvider,
  SsoErrorCode,
} from '../types/auth';

/** How each provider is named on screen. */
export const SOCIAL_PROVIDER_NAMES: Record<SocialProvider, string> = {
  google: 'Google',
  apple: 'Apple',
};

/** Whether a value, from an address or an answer, names a provider. */
export function isSocialProvider(value: unknown): value is SocialProvider {
  return value === 'google' || value === 'apple';
}

/**
 * The provider an account can't have beside `provider`: an account signs in
 * with one of Google or Apple at most.
 */
export function otherSocialProvider(provider: SocialProvider): SocialProvider {
  return provider === 'google' ? 'apple' : 'google';
}

/** A notice's tone: the `Alert` variant each platform renders it with. */
export type SocialSignInNoticeVariant =
  'neutral' | 'info' | 'success' | 'warning' | 'error';

/** What to say about a social sign-in that didn't sign in. */
export interface SocialSignInNotice {
  variant: SocialSignInNoticeVariant;
  title?: string;
  body: string;
}

/**
 * The words before a provider's name on its button. Apple and Google allow
 * only these: "Continue with", "Sign in with" and "Sign up with".
 */
export type SocialButtonWording = 'continue' | 'sign-in' | 'sign-up';

export const SOCIAL_BUTTON_WORDS: Record<SocialButtonWording, string> = {
  continue: 'Continue with',
  'sign-in': 'Sign in with',
  'sign-up': 'Sign up with',
};

/**
 * Why a social sign-in didn't sign in: the website's `sso_error` codes, plus
 * `rate_limited`, which only a request is answered with.
 */
export type SocialSignInRefusal = SsoErrorCode | 'rate_limited';

/** What a sign-in notice knows, beside how to name the provider. */
interface SignInNoticeContext {
  /** The provider the sign-in was with, when it's known. */
  provider?: SocialProvider | undefined;
  /** How the account with that email signs in (`account_exists`). */
  signsInWith?: SocialProvider | undefined;
}

/**
 * Each notice, given how to name the provider. The website's error address
 * carries only the code, so its copy names both providers; an app knows which
 * one it used.
 */
const NOTICES: Record<
  SocialSignInRefusal,
  (providerName: string, context: SignInNoticeContext) => SocialSignInNotice
> = {
  cancelled: () => ({ variant: 'neutral', body: 'Sign-in cancelled.' }),
  expired: () => ({
    variant: 'warning',
    title: 'That sign-in expired',
    body: 'It took too long. Try again.',
  }),
  unavailable: (providerName) => ({
    variant: 'warning',
    title: 'Couldn’t sign you in',
    body: `${providerName} couldn’t be reached, or turned the sign-in down. Try again in a few minutes.`,
  }),
  // A sign-in never links an account. One that signs in with a provider can't
  // have the other too, so send them to the button that works.
  account_exists: (providerName, { provider, signsInWith }) => {
    const title = 'You already have a Dō account';
    if (!signsInWith) {
      return {
        variant: 'info',
        title,
        body: `That email is already in use. Log in with your password, then connect ${providerName} in Settings → Account.`,
      };
    }
    const method = SOCIAL_PROVIDER_NAMES[signsInWith];
    if (!provider) {
      return {
        variant: 'info',
        title,
        body: `It signs in with ${method}. Continue with ${method}, using the ${method} account you use for Dō.`,
      };
    }
    return {
      variant: 'info',
      title,
      body:
        signsInWith === provider
          ? `It signs in with a different ${method} account. Continue with that ${method} account instead.`
          : `It signs in with ${method}. Continue with ${method} instead.`,
    };
  },
  email_unverified: (providerName) => ({
    variant: 'error',
    title: 'Email not verified',
    body: `Your ${providerName} account hasn’t verified its email, so Dō can’t use it. Verify it there, or log in with your password.`,
  }),
  email_missing: () => ({
    variant: 'error',
    title: 'No email shared',
    body: 'Dō needs an email address, and none was shared. Try again and allow your email, or log in with your password.',
  }),
  sign_up_closed: () => ({
    variant: 'info',
    title: 'Dō is invite-only for now',
    body: 'There’s no account for that sign-in, and new accounts need an invitation.',
  }),
  rate_limited: () => ({
    variant: 'warning',
    title: 'Too many tries',
    body: 'Wait a minute, then try again.',
  }),
};

/**
 * The notice for why a social sign-in didn't sign in. Pass the provider when
 * it's known and the copy names it; without one, it names both. For
 * `account_exists`, pass how that account signs in when the API says
 * (`socialSignInMethodFor`, or the website's `sso_method`).
 */
export function socialSignInNotice(
  refusal: SocialSignInRefusal,
  provider?: SocialProvider,
  signsInWith?: SocialProvider
): SocialSignInNotice {
  return NOTICES[refusal](
    provider ? SOCIAL_PROVIDER_NAMES[provider] : 'Google or Apple',
    { provider, signsInWith }
  );
}

/** For a refusal this build can't name: the sign-in still didn't work. */
export const UNKNOWN_SOCIAL_SIGN_IN_NOTICE: SocialSignInNotice = {
  variant: 'warning',
  title: 'Couldn’t sign you in',
  body: 'Try again, or log in with your password.',
};

/**
 * Why the API refused an app's social sign-in, or an answer to a pending
 * sign-up, going by the error's `code`:
 *
 * - `NOT_FOUND`: the pending sign-up lapsed, was answered, or is unknown.
 * - `UNAUTHENTICATED`: the provider's token, nonce or code was refused. A
 *   nonce works once and Apple's code lapses in five minutes, so, like a
 *   lapsed pending sign-up, it means starting again.
 * - `RESOURCE_CONFLICT`: the email already has an account, which a sign-in
 *   never links.
 * - `FORBIDDEN`: there's no account, and sign-ups are closed.
 * - `SERVER_ERROR`: the provider couldn't be reached.
 * - `RATE_LIMITED`: too many tries.
 *
 * Undefined for anything else, such as an email the provider hasn't verified
 * (the API's message says which) or a dropped connection.
 */
export function socialSignInRefusalFor(
  error: unknown
): SocialSignInRefusal | undefined {
  const code =
    typeof error === 'object' && error !== null
      ? (error as { code?: unknown }).code
      : undefined;

  switch (code) {
    case API_ERROR_CODES.NOT_FOUND:
    case API_ERROR_CODES.UNAUTHENTICATED:
      return 'expired';
    case API_ERROR_CODES.RESOURCE_CONFLICT:
      return 'account_exists';
    case API_ERROR_CODES.FORBIDDEN:
      return 'sign_up_closed';
    case API_ERROR_CODES.SERVER_ERROR:
      return 'unavailable';
    case API_ERROR_CODES.RATE_LIMITED:
      return 'rate_limited';
    default:
      return undefined;
  }
}

/**
 * How the account with the email signs in, from an app's `account_exists`
 * refusal (its `meta.signs_in_with`). Undefined when it has only a password,
 * or the refusal doesn't say.
 */
export function socialSignInMethodFor(
  error: unknown
): SocialProvider | undefined {
  const meta =
    typeof error === 'object' && error !== null
      ? (error as { meta?: unknown }).meta
      : undefined;
  const method =
    typeof meta === 'object' && meta !== null
      ? (meta as { signs_in_with?: unknown }).signs_in_with
      : undefined;
  return isSocialProvider(method) ? method : undefined;
}

/**
 * After “I already have an account”: how to add the provider once signed in,
 * unless that account has the other one, since an account has one at most.
 */
export function socialConnectHint(
  provider: SocialProvider
): SocialSignInNotice {
  const name = SOCIAL_PROVIDER_NAMES[provider];
  const other = SOCIAL_PROVIDER_NAMES[otherSocialProvider(provider)];
  return {
    variant: 'info',
    title: `Log in to connect ${name}`,
    body: `Log in to your account, then connect ${name} in Settings → Account, unless it already signs in with ${other}: an account uses one of the two.`,
  };
}

/**
 * How confirming with, or connecting, a provider ended, in the API's terms
 * (`AccountFlowResult`): the website lands on
 * `/settings?account_flow=…&result=…`, and the apps meet the same outcomes as
 * answers (a 503 when Apple can't be reached, connect controls held back
 * while two-factor is on). Where the outcome is a sign-in's, so are the words.
 * Each platform adds its own next step.
 */
/** What an account flow notice knows, beside how to name the provider. */
interface AccountFlowNoticeContext {
  /** The provider the flow was with, when it's known. */
  provider?: SocialProvider | undefined;
  /** Whether the account has a password: switching providers may need one. */
  hasPassword: boolean;
}

const ACCOUNT_FLOW_NOTICES: Record<
  Exclude<AccountFlowResult, 'cancelled'>,
  (
    providerName: string,
    context: AccountFlowNoticeContext
  ) => SocialSignInNotice
> = {
  confirmed: () => ({
    variant: 'success',
    title: 'Confirmed',
    body: 'For the next ten minutes you can make changes here without confirming again.',
  }),
  linked: (providerName) => ({
    variant: 'success',
    title: `${providerName} connected`,
    body: `You can sign in to Dō with ${providerName} now.`,
  }),
  not_linked: (providerName) => ({
    variant: 'warning',
    title: `That ${providerName} account isn’t connected`,
    body: `Sign in again with the ${providerName} account you use for Dō.`,
  }),
  mismatch: (providerName) => ({
    variant: 'warning',
    title: `That’s a different ${providerName} account`,
    body: `Sign in with the ${providerName} account connected to Dō.`,
  }),
  confirmation_required: (providerName) => ({
    variant: 'info',
    title: 'Confirm it’s you first',
    body: `Connecting ${providerName} needs a recent confirmation.`,
  }),
  two_factor_on: () => ({
    variant: 'info',
    title: 'Turn off two-factor first',
    body: 'Google and Apple sign-ins skip the two-factor code, so they can’t be connected while it’s on.',
  }),
  already_linked: (providerName) => ({
    variant: 'info',
    title: `Another ${providerName} account is already connected`,
    body: 'Disconnect it first, then connect the one you want.',
  }),
  // Connecting the other provider: switching is disconnecting the one the
  // account uses first, after adding a password if it's the only way in.
  other_provider_linked: (providerName, { provider, hasPassword }) => {
    const addPassword = hasPassword ? '' : 'add a password, ';
    if (!provider) {
      return {
        variant: 'info',
        title: 'Your account already uses Google or Apple',
        body: `An account uses Google or Apple, not both. To switch, ${addPassword}disconnect the one it uses, then connect the other.`,
      };
    }
    const connected = SOCIAL_PROVIDER_NAMES[otherSocialProvider(provider)];
    return {
      variant: 'info',
      title: `Your account signs in with ${connected}`,
      body: `An account uses Google or Apple, not both. To use ${providerName} instead, ${addPassword}disconnect ${connected}, then connect ${providerName}.`,
    };
  },
  identity_in_use: (providerName) => ({
    variant: 'info',
    title: `That ${providerName} account is used by another Dō account`,
    body: `It stays with that account. Log in there to use it, or connect a different ${providerName} account here.`,
  }),
  // The provider's sign-in lapsed, or couldn't be reached: a sign-in's words.
  expired: (providerName) => NOTICES.expired(providerName, {}),
  unavailable: (providerName) => ({
    ...NOTICES.unavailable(providerName, {}),
    title: `Couldn’t reach ${providerName}`,
  }),
  // The account's own email, not the provider's: each platform's gate offers
  // a new link.
  email_unverified: () => ({
    variant: 'info',
    title: 'Verify your email first',
    body: 'Open the link we sent to your email, then try again.',
  }),
};

/** For an outcome this build doesn't know yet. */
export const UNKNOWN_ACCOUNT_FLOW_NOTICE: SocialSignInNotice = {
  variant: 'warning',
  title: 'That didn’t finish',
  body: 'Start again from here.',
};

function isAccountFlowNotice(
  result: string
): result is keyof typeof ACCOUNT_FLOW_NOTICES {
  return Object.prototype.hasOwnProperty.call(ACCOUNT_FLOW_NOTICES, result);
}

/** What else an account flow notice can be told. */
export interface AccountFlowNoticeOptions {
  /**
   * Whether the account has a password, so switching providers says to add
   * one first when it hasn't. Assumed when not given.
   */
  hasPassword?: boolean;
}

/**
 * The notice for how a confirm or connect with a provider ended, or null when
 * there's nothing to say (`cancelled`: backing out was the owner's choice).
 * Pass the provider when it's known; otherwise it says "Google or Apple". For
 * `other_provider_linked`, that's the provider the flow tried to connect.
 */
export function accountFlowNotice(
  result: string,
  provider?: SocialProvider,
  { hasPassword = true }: AccountFlowNoticeOptions = {}
): SocialSignInNotice | null {
  if (result === 'cancelled') return null;
  if (!isAccountFlowNotice(result)) return UNKNOWN_ACCOUNT_FLOW_NOTICE;
  return ACCOUNT_FLOW_NOTICES[result](
    provider ? SOCIAL_PROVIDER_NAMES[provider] : 'Google or Apple',
    { provider, hasPassword }
  );
}
